import { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, Gavel, Tag, ChevronRight, Sparkles } from 'lucide-react';
import { BannerMediaLayer, BannerMediaControls } from '@/components/ui/BannerMedia';
import { BannerTextColorControl, withAlpha } from '@/components/ui/BannerTextColorControl';
import { AdSheetModal, type AdSlide } from '@/components/ui/AdSheetModal';
import { useStore } from '@/store/useStore';

/** Each announcement slide owns its media so the three banners never share one file. */
export const homeBannerKey = (index: number) => `bg_banner_home_${index + 1}`;

/** Admin-chosen text colour for a slide, stored next to its media. */
export const homeBannerTextKey = (index: number) => `bg_banner_home_text_${index + 1}`;

const SLIDES: AdSlide[] = [
    {
        id: 'p2p',
        title: 'P2P Deals — Buy usernames safely',
        subtitle: 'Protected escrow + bot transfer from trusted sellers.',
        cta: 'Start',
        icon: <ShieldCheck className="w-5 h-5 text-emerald-300" />,
        gradient: 'from-emerald-500/30 via-emerald-400/10 to-transparent',
        page: 'store',
    },
    {
        id: 'auction',
        title: 'Auction House — bid on rare usernames',
        subtitle: 'Live auctions with 1-step bidding and full transparency.',
        cta: 'Go',
        icon: <Gavel className="w-5 h-5 text-purple-300" />,
        gradient: 'from-purple-500/30 via-fuchsia-500/10 to-transparent',
        page: 'auctions',
    },
    {
        id: 'list',
        title: 'Sell faster — list your username',
        subtitle: 'Set a store price, put it to auction, or start a P2P deal.',
        cta: 'List',
        icon: <Tag className="w-5 h-5 text-sky-300" />,
        gradient: 'from-sky-500/30 via-cyan-400/10 to-transparent',
        page: 'my_user',
    },
];

const STEP_MS = 4200;
const TICK_MS = 40;

interface AnnouncementBannerProps {
    onNavigate: (page: string, category?: string) => void;
    className?: string;
    /** Disable the auto-advance timer (used by previews/tests) */
    autoPlay?: boolean;
}

export const AnnouncementBanner = ({ onNavigate, className = '', autoPlay = true }: AnnouncementBannerProps) => {
    const { tileBgs } = useStore();
    const trackRef = useRef<HTMLDivElement>(null);
    const [active, setActive] = useState(0);
    const [progress, setProgress] = useState(0);
    const [sheet, setSheet] = useState<{ open: boolean; index: number }>({ open: false, index: 0 });
    const [pickerOpen, setPickerOpen] = useState(false);
    const { t } = useTranslation('home');

    const colorOf = (slideIdx: number) =>
        (tileBgs as Record<string, string>)?.[homeBannerTextKey(slideIdx)] || '#FFFFFF';

    const onScroll = () => {
        const el = trackRef.current;
        if (!el) return;
        const w = el.clientWidth || 1;
        const idx = Math.round(el.scrollLeft / w);
        setActive(Math.max(0, Math.min(SLIDES.length - 1, idx)));
    };

    const go = useCallback((idx: number) => {
        trackRef.current?.scrollTo({ left: idx * (trackRef.current?.clientWidth || 0), behavior: 'smooth' });
        setActive(idx);
    }, []);

    // Fill the active capsule, then flip to the next slide.
    useEffect(() => {
        if (!autoPlay || sheet.open || pickerOpen) return;
        setProgress(0);
        const step = 100 / (STEP_MS / TICK_MS);
        const timer = setInterval(() => {
            setProgress(prev => {
                if (prev >= 100) {
                    go((active + 1) % SLIDES.length);
                    return 0;
                }
                return prev + step;
            });
        }, TICK_MS);
        return () => clearInterval(timer);
    }, [autoPlay, sheet.open, pickerOpen, active, go]);

    const openSheet = (index: number) => {
        setSheet({ open: true, index });
    };

    return (
        <div className={`relative ${className}`}>
            <div
                ref={trackRef}
                onScroll={onScroll}
                className="flex snap-x snap-mandatory overflow-x-auto no-scrollbar gap-3"
                style={{ scrollSnapType: 'x mandatory' }}
            >
                {SLIDES.map((s, slideIdx) => (
                    <div
                        key={s.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => openSheet(slideIdx)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openSheet(slideIdx); } }}
                        className="relative w-full shrink-0 snap-start overflow-hidden rounded-[24px] border border-white/[0.06] text-left cursor-pointer active:scale-[0.985] transition-transform"
                        style={{ aspectRatio: '508 / 136' }}
                    >
                        <BannerMediaLayer bgKey={homeBannerKey(slideIdx)} slideIndex={slideIdx} activeSlide={active} />
                        <div className={`absolute inset-0 bg-gradient-to-r ${s.gradient}`} />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                        <Sparkles className="absolute -right-6 -top-6 w-28 h-28 text-white/[0.06] rotate-12" />
                        <div className="relative h-full flex items-center gap-3 px-4">
                            <div className="w-11 h-11 shrink-0 rounded-2xl glass shadow-[0_0_24px_rgba(0,0,0,.45)] flex items-center justify-center">
                                {s.icon}
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="text-[13px] font-bold leading-tight truncate" style={{ color: colorOf(slideIdx) }}>
                                    {t(s.id + 'Title', s.title)}
                                </p>
                                <p className="text-[10px] font-medium leading-snug mt-0.5 line-clamp-1" style={{ color: withAlpha(colorOf(slideIdx), 0.72) }}>
                                    {t(s.id + 'Subtitle', s.subtitle)}
                                </p>
                            </div>
                            <div className="shrink-0 flex items-center gap-1 rounded-full glass-chip px-3 py-1.5 text-[11px] font-bold" style={{ color: colorOf(slideIdx) }}>
                                {t(s.id + 'Cta', s.cta)}
                                <ChevronRight size={12} className="rtl-flip" />
                            </div>
                        </div>

                        {/* Admin-only: this slide's own image/video + text colour */}
                        <BannerMediaControls bgKey={homeBannerKey(slideIdx)} className="top-2 right-2" />
                        <BannerTextColorControl colorKey={homeBannerTextKey(slideIdx)} className="top-2 left-2" onOpenChange={setPickerOpen} />
                    </div>
                ))}
            </div>

            {/* Animated capsule dots — the fill travels from one capsule to the next */}
            {SLIDES.length > 1 && (
                <div className="flex items-center justify-center gap-1.5 mt-2">
                    {SLIDES.map((s, idx) => {
                        const isActive = idx === active;
                        return (
                            <div
                                key={s.id}
                                onClick={() => go(idx)}
                                className={`h-1.5 rounded-full cursor-pointer transition-all duration-300 relative overflow-hidden ${
                                    isActive ? 'w-6 bg-white/20' : 'w-1.5 bg-white/20 hover:bg-white/40'
                                }`}
                                role="button"
                            >
                                {isActive && (
                                    <motion.div
                                        className="h-full bg-white rounded-full"
                                        style={{ width: `${progress}%` }}
                                        transition={{ ease: 'linear' }}
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <AdSheetModal
                slides={SLIDES}
                isOpen={sheet.open}
                startIndex={sheet.index}
                onClose={() => setSheet(s => ({ ...s, open: false }))}
                onNavigate={onNavigate}
                bgKeyFor={homeBannerKey}
                textColorFor={colorOf}
                titleFor={s => t(s.id + 'Title', s.title)}
                subtitleFor={s => t(s.id + 'Subtitle', s.subtitle)}
                ctaFor={s => t(s.id + 'Cta', s.cta)}
            />
        </div>
    );
};

export default AnnouncementBanner;